import { cn } from '@quadralize/utils'
import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { motion } from 'framer-motion'
import * as React from 'react'
import { buttonVariants } from './button.variants'

const spinnerVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
}

interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  isLoading?: boolean
  justify?: 'start' | 'center' | 'end' | 'between'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      startIcon,
      endIcon,
      children,
      disabled,
      justify,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    const alignmentClasses = {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    }

    const currentJustify =
      justify || (className?.includes('justify-between') ? 'between' : 'center')

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isLoading || disabled}
        {...props}
      >
        <motion.div
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          tabIndex={-1}
          className={cn(
            'relative flex items-center gap-2 overflow-hidden min-w-0 h-full outline-none',
            !asChild && 'w-full',
            alignmentClasses[currentJustify as keyof typeof alignmentClasses],
          )}
        >
          {isLoading && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <motion.div
                key="loader"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={spinnerVariants}
                className="flex items-center justify-center"
              >
                <div
                  data-testid="button-loader"
                  className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                />
              </motion.div>
            </div>
          )}

          <span
            className={cn(
              'flex items-center gap-2 transition-opacity duration-200 min-w-0 flex-1 outline-none',
              alignmentClasses[currentJustify as keyof typeof alignmentClasses],
              currentJustify === 'between' && 'w-full',
              isLoading ? 'opacity-0' : 'opacity-100',
            )}
          >
            {!asChild ? (
              <>
                {startIcon && <span>{startIcon}</span>}
                {children}
                {endIcon && <span>{endIcon}</span>}
              </>
            ) : (
              children
            )}
          </span>
        </motion.div>
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, type ButtonProps }
